import React, { useState } from "react";
import { api, extractError } from "../lib/api";
import { useAuth, isAdmin } from "../context/AuthContext";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const ENTITY_LABELS = {
  purchase_contract: "purchase contract",
  purchase: "purchase / truck",
  gate_pass: "gate pass",
  party: "party",
  stack_sample_intake: "stack sample intake",
  voucher: "voucher",
  stack: "stack",
  production_yield: "production yield",
};

export default function AdminPurgeButton({
  entityType,
  entityId,
  entityLabel,
  onDeleted,
  size = "sm",
  variant = "ghost",
  className = "text-red-600 shrink-0",
  testId,
  title = "Delete permanently (admin)",
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isAdmin(user) || entityId == null || entityId === "") {
    return null;
  }

  const label = entityLabel || preview?.label || `#${entityId}`;
  const typeLabel = ENTITY_LABELS[entityType] || entityType;

  const loadPreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const res = await api.get("/admin/purge-preview", {
        params: { entity_type: entityType, entity_id: entityId },
      });
      setPreview(res.data);
      setOpen(true);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  const runPurge = async () => {
    if (!preview) return;
    setSubmitting(true);
    try {
      await api.post("/admin/purge", {
        entity_type: entityType,
        entity_id: Number(entityId),
        force: Boolean(preview.requires_force),
      });
      toast.success(`${label} permanently deleted`);
      setOpen(false);
      setPreview(null);
      onDeleted?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size={size}
        variant={variant}
        className={className}
        onClick={loadPreview}
        disabled={loading}
        aria-label={title}
        title={title}
        data-testid={testId || `admin-purge-${entityType}-${entityId}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
        {loading ? <span className="sr-only">Loading…</span> : null}
      </Button>

      <AlertDialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setPreview(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
              Permanently delete {typeLabel}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  This will permanently remove <strong className="text-foreground">{label}</strong> and cannot be undone.
                </p>
                {preview?.summary_lines?.length > 0 && (
                  <ul className="list-disc pl-5 space-y-1 text-foreground/90">
                    {preview.summary_lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                )}
                {preview?.requires_force && (
                  <p className="text-destructive font-medium">
                    Linked records are still active — they will be removed or unlinked.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runPurge}
              disabled={submitting || !preview}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {submitting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
